/**
 * realtime-client — Socket.io client pra notificações em tempo real (FEAT-V2-028).
 *
 * Comportamento:
 *   - Conecta com JWT no handshake (`auth: { token }`)
 *   - Junta automaticamente à room `user:<userId>` no server (handshake side)
 *   - Recebe evento `notification:new` → invalida cache TanStack Query +
 *     dispara toast (com navegação opcional via payload.linkUrl)
 *   - Reconexão automática (Socket.io built-in com backoff exponencial)
 *   - Polling 60s do TanStack Query continua como fallback automático se WS
 *     desconectar — não precisa código extra
 *
 * Memória feedback_design_system_notifications (21/05) + memória
 * project_mega_pr_decisoes.md (25/05): toast deve permitir navegar via
 * payload.linkUrl (regra design-system).
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';

/**
 * Lê token do localStorage diretamente (em vez de importar `getAuthToken`
 * do `./api`) — evita que mocks de `@/lib/api` em testes legados quebrem
 * componentes que usam realtime indiretamente (ex.: AppShell renderizado
 * em MaisPage.test). Lógica idêntica ao api.getAuthToken.
 */
function readJwt(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('igm_jwt');
  } catch {
    return null;
  }
}

interface NotificationNewPayload {
  id: string;
  type: string;
  title: string;
  body: string;
  linkUrl: string | null;
}

interface UseRealtimeNotificationsOptions {
  /**
   * Callback opcional pra render toast quando notification:new chega.
   * Caller é responsável por integrar com Toast provider do app
   * (separado pra que o client não dependa de UI específica).
   */
  onNewNotification?: (payload: NotificationNewPayload) => void;
}

/**
 * Hook que mantém a conexão WS aberta enquanto o componente está montado.
 * Tipicamente montado UMA vez no AppShell (após login). Múltiplos hooks
 * com mesmo userId compartilham a mesma instância via guard de ref.
 */
export function useRealtimeNotifications(
  userId: string | null,
  options: UseRealtimeNotificationsOptions = {},
): void {
  const qc = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!userId || userId === 'anonymous') return;
    const token = readJwt();
    if (!token) return;

    // Conecta no mesmo host do API (Vite proxy + nginx em prod).
    // Socket.io decide automaticamente entre WS e polling baseado em capability.
    const socket = io({
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.info('[realtime] socket conectado', socket.id);
    });

    socket.on('auth_error', (e: { error: string }) => {
      console.warn('[realtime] auth_error — fallback polling assume', e);
    });

    socket.on('notification:new', (payload: NotificationNewPayload) => {
      // Invalida cache pra TanStack Query refetch a lista imediato (UX:
      // sino atualiza badge instantâneo).
      void qc.invalidateQueries({ queryKey: ['v1', 'notifications', userId] });
      // Caller decide o que fazer com o toast.
      options.onNewNotification?.(payload);
    });

    socket.on('disconnect', (reason: string) => {
      // Polling 60s continua refetch — não precisa fallback explícito aqui.
      console.info('[realtime] socket desconectado', reason);
    });

    return () => {
      socket.off('notification:new');
      socket.off('auth_error');
      socket.off('connect');
      socket.off('disconnect');
      socket.disconnect();
      socketRef.current = null;
    };
    // options.onNewNotification deliberadamente NÃO no deps — caller pode
    // re-criar a referência sem reconectar o socket. Captura via closure
    // do useRef no momento da chamada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, qc]);
}
